import { useMemo, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import {
  calculateRealEstateSaleWorksheet,
  formatCurrency,
  PROPERTY_TYPES,
  type PropertyType,
  type RealEstateSaleWorksheetInput,
} from "@/lib/realEstateSaleWorksheet";
import { openDepreciationReportPrint } from "@/lib/depreciationWorksheetPrint";

const EMPTY_WORKSHEET: RealEstateSaleWorksheetInput = {
  propertyType: "residential_rental",
  acquisitionDate: "",
  saleDate: "",
  purchasePrice: 0,
  acquisitionCosts: 0,
  improvements: 0,
  sellingExpenses: 0,
  sellingPrice: 0,
  landValue: 0,
  manualAnnualDepreciation: 0,
  actualDepreciationOverride: null,
};

function WorksheetGlyph({ className = "" }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M6.5 3.5h8l3 3v14H6.5a2 2 0 01-2-2v-13a2 2 0 012-2z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M14.5 3.5v4h3M8 11h8M8 14.5h8M8 18h4" />
    </svg>
  );
}

function parseAmount(value: string): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
}

function CurrencyInput({ label, value, onChange, note }: { label: string; value: number; onChange: (value: number) => void; note?: string }) {
  return (
    <label className="block">
      <span className="text-sm font-semibold text-[oklch(0.21_0.045_240)]">{label}</span>
      {note && <span className="mt-0.5 block text-xs text-[oklch(0.43_0.035_240)]">{note}</span>}
      <div className="relative mt-1.5">
        <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-sm font-bold text-[oklch(0.46_0.04_240)]">$</span>
        <input
          type="number"
          min="0"
          step="0.01"
          inputMode="decimal"
          value={value || ""}
          onChange={(event) => onChange(parseAmount(event.target.value))}
          className="w-full rounded-xl border border-[oklch(0.81_0.03_240)] bg-white py-2.5 pl-7 pr-3 text-sm font-semibold tabular-nums text-[oklch(0.14_0.045_240)] shadow-sm outline-none transition focus:border-[oklch(0.64_0.14_75)] focus:ring-2 focus:ring-[oklch(0.8_0.1_75/0.35)]"
        />
      </div>
    </label>
  );
}

function ResultRow({ label, value, emphasized = false, tone = "navy" }: { label: string; value: number; emphasized?: boolean; tone?: "navy" | "green" | "gold" | "red" }) {
  const valueClass = tone === "green"
    ? "text-[oklch(0.39_0.12_150)]"
    : tone === "gold"
      ? "text-[oklch(0.36_0.09_75)]"
      : tone === "red"
        ? "text-[oklch(0.52_0.17_28)]"
        : "text-[oklch(0.14_0.045_240)]";
  return (
    <div className={`flex items-center justify-between gap-4 ${emphasized ? "border-t border-[oklch(0.8_0.04_75)] pt-3" : ""}`}>
      <span className={`${emphasized ? "font-bold" : "font-medium"} text-sm text-[oklch(0.29_0.04_240)]`}>{label}</span>
      <span className={`${emphasized ? "text-xl" : "text-base"} ${valueClass} font-bold tabular-nums`}>{formatCurrency(value)}</span>
    </div>
  );
}

export default function DepreciationWorksheet() {
  const [input, setInput] = useState<RealEstateSaleWorksheetInput>(EMPTY_WORKSHEET);
  const result = useMemo(() => calculateRealEstateSaleWorksheet(input), [input]);
  const property = PROPERTY_TYPES.find((option) => option.value === input.propertyType) ?? PROPERTY_TYPES[0]!;
  const totalResultTone = result.totalGainOrLoss >= 0 ? "green" : "red";
  const remainingResultTone = result.remainingGainOrLoss >= 0 ? "gold" : "red";

  const update = <K extends keyof RealEstateSaleWorksheetInput>(key: K, value: RealEstateSaleWorksheetInput[K]) => {
    setInput((current) => ({ ...current, [key]: value }));
  };

  return (
    <DashboardLayout>
      <main className="mx-auto w-full max-w-7xl space-y-5 pb-8 animate-fade-slide-up">
        <header className="rounded-2xl border border-[oklch(0.8_0.035_240)] bg-[linear-gradient(118deg,oklch(0.14_0.06_240),oklch(0.21_0.075_230))] px-5 py-5 text-white shadow-[0_16px_32px_oklch(0.17_0.05_240/0.18)] sm:px-7">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="flex items-start gap-4">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-white/15 bg-[oklch(0.72_0.14_75/0.18)] text-[oklch(0.84_0.14_82)]"><WorksheetGlyph className="h-6 w-6" /></span>
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[oklch(0.82_0.13_80)]">Tax & Controller Workpaper</p>
                <h1 className="mt-0.5 text-2xl font-bold sm:text-3xl" style={{ fontFamily: "'Playfair Display', serif" }}>Depreciation Calc/ WkSht</h1>
                <p className="mt-1 text-sm text-[oklch(0.89_0.015_240)]">Sale profit/loss first. Depreciation and recapture review kept on a separate line.</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={() => openDepreciationReportPrint(input, result)} className="rounded-lg bg-[oklch(0.73_0.14_75)] px-3 py-2 text-xs font-bold text-[oklch(0.14_0.045_240)] shadow-sm transition hover:bg-[oklch(0.79_0.14_80)]">Print / Save as PDF</button>
              <button type="button" onClick={() => setInput(EMPTY_WORKSHEET)} className="rounded-lg border border-white/30 bg-white/10 px-3 py-2 text-xs font-bold text-white transition hover:bg-white/20">Clear worksheet</button>
            </div>
          </div>
        </header>

        <section className="grid gap-5 xl:grid-cols-[1.1fr_0.9fr]">
          <div className="rounded-2xl border border-[oklch(0.83_0.025_240)] bg-white p-5 shadow-sm sm:p-6">
            <div className="mb-5 flex items-start gap-3">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[oklch(0.16_0.06_240)] text-sm font-bold text-[oklch(0.83_0.13_80)]">1</span>
              <div>
                <h2 className="text-xl font-bold text-[oklch(0.14_0.045_240)]" style={{ fontFamily: "'Playfair Display', serif" }}>Real Estate Sale Worksheet</h2>
                <p className="mt-1 text-sm text-[oklch(0.4_0.035_240)]">Complete the sale in working order: purchase through selling price.</p>
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <label className="block sm:col-span-2">
                <span className="text-sm font-semibold text-[oklch(0.21_0.045_240)]">Type of asset</span>
                <select value={input.propertyType} onChange={(event) => update("propertyType", event.target.value as PropertyType)} className="mt-1.5 w-full rounded-xl border border-[oklch(0.81_0.03_240)] bg-white px-3 py-2.5 text-sm font-semibold text-[oklch(0.14_0.045_240)] shadow-sm outline-none focus:border-[oklch(0.64_0.14_75)] focus:ring-2 focus:ring-[oklch(0.8_0.1_75/0.35)]">
                  {PROPERTY_TYPES.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="text-sm font-semibold text-[oklch(0.21_0.045_240)]">Acquisition Date</span>
                <input type="date" value={input.acquisitionDate} onChange={(event) => update("acquisitionDate", event.target.value)} className="mt-1.5 w-full rounded-xl border border-[oklch(0.81_0.03_240)] bg-white px-3 py-2.5 text-sm font-semibold text-[oklch(0.14_0.045_240)] shadow-sm outline-none focus:border-[oklch(0.64_0.14_75)] focus:ring-2 focus:ring-[oklch(0.8_0.1_75/0.35)]" />
              </label>
              <label className="block">
                <span className="text-sm font-semibold text-[oklch(0.21_0.045_240)]">Sale Date</span>
                <input type="date" value={input.saleDate} onChange={(event) => update("saleDate", event.target.value)} className="mt-1.5 w-full rounded-xl border border-[oklch(0.81_0.03_240)] bg-white px-3 py-2.5 text-sm font-semibold text-[oklch(0.14_0.045_240)] shadow-sm outline-none focus:border-[oklch(0.64_0.14_75)] focus:ring-2 focus:ring-[oklch(0.8_0.1_75/0.35)]" />
              </label>
              <CurrencyInput label="Purchase Price" value={input.purchasePrice} onChange={(value) => update("purchasePrice", value)} />
              <CurrencyInput label="Acquisition Costs" value={input.acquisitionCosts} onChange={(value) => update("acquisitionCosts", value)} note="Added to basis" />
              <CurrencyInput label="Improvements" value={input.improvements} onChange={(value) => update("improvements", value)} note="Capital improvements" />
              <CurrencyInput label="Selling Expenses" value={input.sellingExpenses} onChange={(value) => update("sellingExpenses", value)} note="Commissions, closing costs, etc." />
              <CurrencyInput label="Selling Price" value={input.sellingPrice} onChange={(value) => update("sellingPrice", value)} />
            </div>

            <div className="mt-6 rounded-xl border border-[oklch(0.84_0.04_75)] bg-[oklch(0.985_0.018_75)] p-4">
              <p className="mb-3 text-xs font-bold uppercase tracking-[0.12em] text-[oklch(0.4_0.055_75)]">Sale result before depreciation</p>
              <div className="space-y-3">
                <ResultRow label="Net sale proceeds" value={result.netProceeds} />
                <ResultRow label="Cost basis before depreciation" value={result.costBasisBeforeDepreciation} />
                <ResultRow label="Net profit/loss before depreciation" value={result.gainOrLossBeforeDepreciation} emphasized tone={result.gainOrLossBeforeDepreciation >= 0 ? "green" : "red"} />
              </div>
            </div>
          </div>

          <div className="space-y-5">
            <section className="rounded-2xl border border-[oklch(0.83_0.025_240)] bg-white p-5 shadow-sm sm:p-6">
              <div className="mb-5 flex items-start gap-3">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[oklch(0.72_0.14_75)] text-sm font-bold text-[oklch(0.13_0.04_240)]">2</span>
                <div>
                  <h2 className="text-xl font-bold text-[oklch(0.14_0.045_240)]" style={{ fontFamily: "'Playfair Display', serif" }}>Depreciation Calc/ WkSht</h2>
                  <p className="mt-1 text-sm text-[oklch(0.4_0.035_240)]">Calculated separately from the sale profit/loss.</p>
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <CurrencyInput label="Land Value" value={input.landValue} onChange={(value) => update("landValue", value)} note="Excluded from building depreciation" />
                {input.propertyType === "manual" ? (
                  <CurrencyInput label="Manual Annual Depreciation" value={input.manualAnnualDepreciation} onChange={(value) => update("manualAnnualDepreciation", value)} />
                ) : (
                  <div className="rounded-xl border border-[oklch(0.85_0.025_240)] bg-[oklch(0.98_0.01_240)] px-4 py-3">
                    <p className="text-xs font-bold uppercase tracking-[0.1em] text-[oklch(0.44_0.04_240)]">Recovery life</p>
                    <p className="mt-1 text-lg font-bold text-[oklch(0.16_0.05_240)]">{property.recoveryYears ? `${property.recoveryYears} years` : "No depreciation"}</p>
                  </div>
                )}
                <label className="block sm:col-span-2">
                  <span className="text-sm font-semibold text-[oklch(0.21_0.045_240)]">Actual depreciation allowed/allowable <span className="font-normal text-[oklch(0.45_0.035_240)]">(optional override)</span></span>
                  <div className="relative mt-1.5">
                    <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-sm font-bold text-[oklch(0.46_0.04_240)]">$</span>
                    <input type="number" min="0" step="0.01" inputMode="decimal" value={input.actualDepreciationOverride ?? ""} onChange={(event) => update("actualDepreciationOverride", event.target.value === "" ? null : parseAmount(event.target.value))} placeholder="Use calculated amount when blank" className="w-full rounded-xl border border-[oklch(0.81_0.03_240)] bg-white py-2.5 pl-7 pr-3 text-sm font-semibold tabular-nums text-[oklch(0.14_0.045_240)] shadow-sm outline-none focus:border-[oklch(0.64_0.14_75)] focus:ring-2 focus:ring-[oklch(0.8_0.1_75/0.35)]" />
                  </div>
                </label>
              </div>

              <div className="mt-5 space-y-2.5 rounded-xl border border-[oklch(0.82_0.035_240)] bg-[oklch(0.985_0.01_240)] p-4">
                <ResultRow label="Purchase value less land value" value={result.depreciableBuildingValue} />
                <ResultRow label="Annual depreciation" value={result.annualDepreciation} />
                <div className="flex items-center justify-between gap-4 text-sm">
                  <span className="font-medium text-[oklch(0.29_0.04_240)]">Holding period</span>
                  <span className="font-bold text-[oklch(0.14_0.045_240)]">{input.acquisitionDate && input.saleDate ? `${result.yearsHeld} years, ${result.remainingMonths} months` : "Enter both dates"}</span>
                </div>
                <ResultRow label="Calculated depreciation" value={result.calculatedDepreciation} />
                <ResultRow label="Depreciation allowed/allowable used" value={result.depreciationAllowedOrAllowable} emphasized tone="gold" />
              </div>
            </section>

            <section className="rounded-2xl border border-[oklch(0.77_0.06_75)] bg-[linear-gradient(135deg,oklch(0.99_0.024_75),oklch(0.975_0.01_240))] p-5 shadow-sm sm:p-6">
              <p className="text-xs font-bold uppercase tracking-[0.13em] text-[oklch(0.39_0.055_75)]">Taxable gain / depreciation separation</p>
              <div className="mt-4 space-y-3">
                <ResultRow label="Adjusted basis after depreciation" value={result.adjustedBasis} />
                <ResultRow label="Total gain/loss after depreciation" value={result.totalGainOrLoss} emphasized tone={totalResultTone} />
                <ResultRow label="Depreciation-related gain / recapture review" value={result.depreciationRelatedGainReview} tone="gold" />
                <ResultRow label="Remaining capital / §1231 gain or loss" value={result.remainingGainOrLoss} emphasized tone={remainingResultTone} />
              </div>
              <div className="mt-5 rounded-lg border border-[oklch(0.81_0.04_240)] bg-white/80 p-3 text-xs leading-5 text-[oklch(0.35_0.035_240)]">
                This is a direct planning workpaper. Use the reviewed depreciation schedule and transaction allocation to complete the return workpapers. For depreciable real property, Form 4797 instructions require separating depreciable property and land where applicable.
              </div>
            </section>
          </div>
        </section>

        <section className="rounded-xl border border-[oklch(0.84_0.022_240)] bg-white px-5 py-4 text-xs leading-5 text-[oklch(0.4_0.035_240)] shadow-sm">
          <span className="font-bold text-[oklch(0.18_0.045_240)]">Reference:</span> This worksheet uses a straight-line annual estimate for the selected building type. IRS Publication 946 lists 27.5 years for residential rental property and 39 years for nonresidential real property; the final return calculation may require additional conventions and asset-level detail. <a className="font-semibold text-[oklch(0.3_0.09_75)] underline underline-offset-2" href="https://www.irs.gov/publications/p946" target="_blank" rel="noreferrer">IRS Pub. 946</a> · <a className="font-semibold text-[oklch(0.3_0.09_75)] underline underline-offset-2" href="https://www.irs.gov/instructions/i4797" target="_blank" rel="noreferrer">Form 4797 Instructions</a>
        </section>
      </main>
    </DashboardLayout>
  );
}
